import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import type { SectionIcon } from '../../../../../components/section/section.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import '../employee-profile-view.css'

export interface EditableFieldDef {
    key: string
    label: string
    multiline?: boolean
}

/**
 * A profile section whose fields are displayed and edited in the same place.
 * Read-only viewers see only fields that hold a value; self/admin always see
 * every field (— when empty) plus an Edit affordance. Owns its edit-mode state.
 */
export const EditableInfoSection = ({
    title,
    icon,
    fields,
    values,
    canEdit,
    lang,
    onSave,
}: {
    title: string
    icon?: SectionIcon
    fields: EditableFieldDef[]
    values: Record<string, string | null>
    canEdit: boolean
    lang: Language
    onSave?: (data: Record<string, string>) => Promise<boolean>
}) => {
    const ph = t.portal
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)

    const hasAnyValue = fields.some((f) => values[f.key])
    if (!canEdit && !hasAnyValue) return null

    return (
        <Section>
            <SectionHeader
                icon={icon}
                title={title}
                actions={
                    canEdit &&
                    onSave &&
                    !isEditing && (
                        <Button
                            variant="secondary"
                            size="small"
                            text={ph.profile.editProfile(lang)}
                            onClick={() => {
                                setDraft(
                                    Object.fromEntries(
                                        fields.map((f) => [f.key, values[f.key] ?? '']),
                                    ),
                                )
                                setIsEditing(true)
                            }}
                        />
                    )
                }
            />
            {isEditing ? (
                <div className="portal-profile__edit-form">
                    {fields.map((f) => (
                        <div key={f.key} className="portal-profile__edit-field">
                            <label className="portal-profile__field-label">{f.label}</label>
                            <TextField
                                as={f.multiline ? 'textarea' : undefined}
                                textAreaProps={f.multiline ? { rows: 3 } : undefined}
                                value={draft[f.key] ?? ''}
                                onChange={(value) =>
                                    setDraft((prev) => ({ ...prev, [f.key]: value }))
                                }
                            />
                        </div>
                    ))}
                    <div className="portal-profile__edit-actions">
                        <Button
                            text={
                                saving
                                    ? ph.profile.savingProfile(lang)
                                    : ph.profile.saveChanges(lang)
                            }
                            size="small"
                            isDisabled={saving}
                            onClick={() => {
                                if (!onSave) return
                                setSaving(true)
                                void onSave(draft).then((ok) => {
                                    setSaving(false)
                                    if (ok) setIsEditing(false)
                                })
                            }}
                        />
                        <Button
                            text={t.common.cancel(lang)}
                            variant="secondary"
                            size="small"
                            onClick={() => setIsEditing(false)}
                        />
                    </div>
                </div>
            ) : (
                fields.map((f) =>
                    canEdit || values[f.key] ? (
                        <FieldRow key={f.key} label={f.label} multiline={f.multiline}>
                            {values[f.key] ?? '—'}
                        </FieldRow>
                    ) : null,
                )
            )}
        </Section>
    )
}
